// src/components/admin/ModalDocumentacionRegimen.js
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner, ListGroup, InputGroup, Badge } from 'react-bootstrap';
import DatosMaestrosService from '../../services/datosMaestrosService';
import { normalizarParaComparar } from '../../utils/textoComparable';
import '../../style/components/GestionDatosMaestros.css';

/**
 * Checklist de documentación de un régimen diferencial agregado por el estudio (los de sistema
 * tienen documentación fija y no llegan acá).
 *
 * Arriba, la documentación genérica que lleva cualquier régimen diferencial (solo lectura).
 * Abajo, la específica de este régimen: se agregan formularios que ya existen en el catálogo de
 * Jubilación o documentos nuevos (se crean al guardar), y se quitan.
 *
 * Al guardar, el backend actualiza también el checklist de los trámites que ya usan el régimen.
 */
const ModalDocumentacionRegimen = ({ show, regimen, onHide, onGuardado }) => {
    const [genericos, setGenericos] = useState([]);
    const [disponibles, setDisponibles] = useState([]);
    const [seleccionados, setSeleccionados] = useState([]); // formularios existentes: { id, nombre }
    const [nuevos, setNuevos] = useState([]); // nombres de documentos a crear
    const [idAAgregar, setIdAAgregar] = useState('');
    const [nombreNuevo, setNombreNuevo] = useState('');
    const [cargando, setCargando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!show || !regimen) return;
        setSeleccionados((regimen.formularios || []).map((f) => ({ id: f.id, nombre: f.nombre })));
        setNuevos([]);
        setIdAAgregar('');
        setNombreNuevo('');
        setError('');
        let activo = true;
        setCargando(true);
        DatosMaestrosService.getDocumentacionBaseRegimenJubilacion()
            .then((res) => {
                if (!activo) return;
                setGenericos(res.data?.genericos || []);
                setDisponibles(res.data?.disponibles || []);
            })
            .catch(() => { if (activo) setError('No se pudo cargar el catálogo de documentación.'); })
            .finally(() => { if (activo) setCargando(false); });
        return () => { activo = false; };
    }, [show, regimen]);

    const disponiblesParaAgregar = useMemo(
        () => disponibles.filter((d) => !seleccionados.some((s) => s.id === d.id)),
        [disponibles, seleccionados]
    );

    const agregarExistente = () => {
        const formulario = disponibles.find((d) => String(d.id) === String(idAAgregar));
        if (!formulario) return;
        setSeleccionados((prev) => [...prev, { id: formulario.id, nombre: formulario.nombre }]);
        setIdAAgregar('');
    };

    const agregarNuevo = () => {
        const nombre = nombreNuevo.trim().replace(/\s+/g, ' ');
        if (!nombre) return;
        const buscado = normalizarParaComparar(nombre);
        // Si ya existe en el catálogo, se usa ese en lugar de crear un duplicado.
        const existente = disponibles.find((d) => normalizarParaComparar(d.nombre) === buscado);
        if (existente) {
            if (!seleccionados.some((s) => s.id === existente.id)) {
                setSeleccionados((prev) => [...prev, { id: existente.id, nombre: existente.nombre }]);
            }
        } else if (genericos.some((g) => normalizarParaComparar(g.nombre) === buscado)) {
            setError(`"${nombre}" ya está incluido en la documentación genérica.`);
            return;
        } else if (!nuevos.some((n) => normalizarParaComparar(n) === buscado)
            && !seleccionados.some((s) => normalizarParaComparar(s.nombre) === buscado)) {
            setNuevos((prev) => [...prev, nombre]);
        }
        setNombreNuevo('');
        setError('');
    };

    const handleGuardar = async () => {
        // Lo que quedó elegido en el select o escrito en el campo sin tocar "Agregar" también se
        // guarda: si no, se perdía en silencio y parecía que el guardado no había funcionado.
        const idsFormulario = seleccionados.map((s) => s.id);
        const nuevosFinal = [...nuevos];
        if (idAAgregar && !idsFormulario.includes(Number(idAAgregar))) {
            idsFormulario.push(Number(idAAgregar));
        }
        const pendiente = nombreNuevo.trim().replace(/\s+/g, ' ');
        if (pendiente) {
            const buscado = normalizarParaComparar(pendiente);
            const existente = disponibles.find((d) => normalizarParaComparar(d.nombre) === buscado);
            if (existente) {
                if (!idsFormulario.includes(existente.id)) idsFormulario.push(existente.id);
            } else if (!genericos.some((g) => normalizarParaComparar(g.nombre) === buscado)
                && !nuevosFinal.some((n) => normalizarParaComparar(n) === buscado)
                && !seleccionados.some((s) => normalizarParaComparar(s.nombre) === buscado)) {
                nuevosFinal.push(pendiente);
            }
        }

        setGuardando(true);
        setError('');
        try {
            const res = await DatosMaestrosService.updateDocumentacionRegimenJubilacion(regimen.id, {
                idsFormulario,
                nuevos: nuevosFinal,
            });
            onGuardado(res.data);
        } catch (err) {
            setError(err?.response?.data?.message || 'No se pudo guardar la documentación.');
        } finally {
            setGuardando(false);
        }
    };

    const sinEspecificos = seleccionados.length === 0 && nuevos.length === 0;

    return (
        <Modal show={show} onHide={onHide} centered scrollable
            className="dm-modal-alto" backdropClassName="dm-modal-alto-backdrop">
            <Modal.Header closeButton>
                <Modal.Title>Documentación — {regimen?.nombre}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {cargando ? (
                    <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
                ) : (
                    <>
                        <h6 className="fw-semibold mb-2">Documentación genérica <span className="text-muted fw-normal small">(siempre incluida)</span></h6>
                        <ListGroup className="mb-4">
                            {genericos.map((g) => (
                                <ListGroup.Item key={g.codigo} className="text-muted">
                                    <i className="bi bi-lock me-2" aria-hidden="true"></i>{g.nombre}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>

                        <h6 className="fw-semibold mb-2">Documentación específica de este régimen</h6>
                        {sinEspecificos ? (
                            <p className="text-muted small">Sin documentación específica: el trámite pide solo la genérica.</p>
                        ) : (
                            <ListGroup className="mb-3">
                                {seleccionados.map((s) => (
                                    <ListGroup.Item key={`f-${s.id}`} className="d-flex justify-content-between align-items-center">
                                        <span><i className="bi bi-check2-square me-2" aria-hidden="true"></i>{s.nombre}</span>
                                        <Button variant="link" size="sm" className="text-danger p-0"
                                            onClick={() => setSeleccionados((prev) => prev.filter((x) => x.id !== s.id))}
                                            aria-label={`Quitar ${s.nombre}`}>
                                            <i className="bi bi-x-lg"></i>
                                        </Button>
                                    </ListGroup.Item>
                                ))}
                                {nuevos.map((n) => (
                                    <ListGroup.Item key={`n-${n}`} className="d-flex justify-content-between align-items-center">
                                        <span>
                                            <i className="bi bi-check2-square me-2" aria-hidden="true"></i>{n}
                                            <Badge bg="info" className="ms-2">Nuevo</Badge>
                                        </span>
                                        <Button variant="link" size="sm" className="text-danger p-0"
                                            onClick={() => setNuevos((prev) => prev.filter((x) => x !== n))}
                                            aria-label={`Quitar ${n}`}>
                                            <i className="bi bi-x-lg"></i>
                                        </Button>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        )}

                        <Form.Label className="small fw-semibold mb-1">Agregar un documento del catálogo</Form.Label>
                        <InputGroup className="mb-3">
                            <Form.Select value={idAAgregar} onChange={(e) => setIdAAgregar(e.target.value)}>
                                <option value="">Seleccione…</option>
                                {disponiblesParaAgregar.map((d) => (
                                    <option key={d.id} value={d.id}>{d.nombre}</option>
                                ))}
                            </Form.Select>
                            <Button variant="outline-primary" onClick={agregarExistente} disabled={!idAAgregar}>
                                Agregar
                            </Button>
                        </InputGroup>

                        <Form.Label className="small fw-semibold mb-1">O escribir un documento nuevo</Form.Label>
                        <InputGroup>
                            <Form.Control
                                type="text"
                                value={nombreNuevo}
                                maxLength={200}
                                placeholder="Ej: Certificado de servicios de la empresa"
                                onChange={(e) => setNombreNuevo(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarNuevo(); } }}
                            />
                            <Button variant="outline-primary" onClick={agregarNuevo} disabled={!nombreNuevo.trim()}>
                                Agregar
                            </Button>
                        </InputGroup>

                        {regimen?.cantidadTramites > 0 && (
                            <Alert variant="warning" className="mt-3 mb-0 small">
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                Este régimen está en {regimen.cantidadTramites} {regimen.cantidadTramites === 1 ? 'trámite' : 'trámites'}.
                                Al guardar, lo agregado aparece como pendiente en su checklist y lo quitado se borra
                                (salvo que ya tenga un archivo adjunto).
                            </Alert>
                        )}
                    </>
                )}
                {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={guardando}>Cancelar</Button>
                <Button variant="primary" onClick={handleGuardar} disabled={guardando || cargando}>
                    {guardando ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalDocumentacionRegimen;
